import { _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentStreamEventType, AgentStreamEventV1 } from '@/model/Agent.model';
import { TERMINAL_STREAM_EVENTS } from '@/model/Agent.model';
import InVivoError from '@/model/InVivoError';
import { RouteService } from '@/support/RouteService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

/**
 * Connection lifecycle status. There is no terminal `error` state: with managed backoff we retry
 * transient drops indefinitely (capped at 30s) rather than giving up, so a dead socket surfaces as
 * `reconnecting`, never a hard failure.
 */
export type AgentStreamConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Narrows the AgentStreamEventV1 union to the single variant for a given `type`. */
export type EventOfType<T extends AgentStreamEventType> = Extract<AgentStreamEventV1, { type: T }>;

const STATUS_CHANNEL = 'status';
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/** JSON.parse can yield null/array/primitive; only a plain object can be a stream event. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && _notNil(value) && !Array.isArray(value);

/**
 * Per-session, domain-agnostic SSE transport for the agent stream. Owns a native `EventSource`,
 * parses named events into typed {@link AgentStreamEventV1} variants, dedups by the Redis stream id
 * (`messageEvent.lastEventId`), and dispatches each typed event + connection status via a private
 * `EventTarget`.
 *
 * Owns its own reconnection (teamEvents #5599 pattern): native `EventSource` retry is disabled — we
 * close the source on every error — and replaced with managed exponential backoff (1s→30s doubling,
 * reset on open). Closing on error means a given source fires `onerror` at most once, so exactly one
 * reconnect is ever in flight (single-in-flight is structural). A terminal event or explicit
 * `disconnect()` sets an `intentionallyClosed` flag so backoff never fights a deliberate close.
 *
 * Holds NO whole-event buffer — derived state (Activity rows, graph nodes) lives in consumer stores.
 * Knows nothing about nodes, graphs, or panels. See the design spec.
 *
 * Registers native listeners (and therefore dedups) ONLY for event types that have a subscriber, plus
 * the terminal types it must always watch to close on. Types nobody consumes (e.g. high-frequency
 * `text_delta`) are never parsed or added to the dedup set, so `#seenIds` doesn't grow on their behalf.
 * Still domain-agnostic — the consumed set is discovered from `subscribe()` calls, not hardcoded.
 */
export class AgentSessionStreamService {
  readonly #url: string;
  readonly #eventTarget = new EventTarget();
  readonly #seenIds = new Set<string>();
  // Event types with at least one subscriber; terminal types are always watched (for terminal-close)
  // even without an explicit subscriber. Drives which native listeners #open() registers.
  readonly #subscribedTypes = new Set<AgentStreamEventType>(TERMINAL_STREAM_EVENTS);
  #status: AgentStreamConnectionStatus = 'connecting';
  #eventSource: EventSource | null = null;
  // Aborted in #teardownSource to remove ALL native listeners on the current source at once. Recreated
  // per #open() (an aborted signal is permanently aborted, so it cannot be reused across reconnects).
  #sourceListeners: AbortController | null = null;
  #reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Set on terminal event / disconnect() so a raced error handler or pending timer never reopens.
  #intentionallyClosed = false;

  constructor(sessionApiId: AgentSessionApiId) {
    this.#url = RouteService.messaging({
      endpoint: 'GET /api/messaging/v1/ai-agents/sessions/{sessionApiId}/stream',
      path: { sessionApiId },
    }).url.href;
  }

  /** Idempotent — opens the connection only if not already connected. */
  connect(): void {
    if (_notNil(this.#eventSource)) {
      return;
    }
    // Clear any pending reconnect timer: a backoff reopen scheduled by a prior onerror would otherwise
    // fire after this connect() and open a SECOND source (leaking a connection + duplicate listeners).
    this.#clearReconnectTimer();
    this.#intentionallyClosed = false;
    this.#open();
  }

  /** Idempotent, reusable — closes the socket, cancels any pending reconnect, retains the dedup Set. */
  disconnect(): void {
    this.#intentionallyClosed = true;
    this.#clearReconnectTimer();
    this.#teardownSource();
    this.#setStatus('closed');
  }

  getConnectionStatus(): AgentStreamConnectionStatus {
    return this.#status;
  }

  /**
   * Registers a status-change listener. Cleanup is driven by the required `AbortSignal` (abort it to
   * remove the listener) — the current house idiom (teamEvents #5599).
   */
  subscribeStatus(listener: () => void, options: { signal: AbortSignal }): void {
    this.#eventTarget.addEventListener(STATUS_CHANNEL, listener, options);
  }

  /**
   * Subscribes an imperative reactor to one event type; the listener receives the typed variant.
   * Cleanup is driven by the required `AbortSignal`. The orchestrator wires all subscriptions in one
   * effect BEFORE calling `connect()`, so no live event is missed; dedup by Redis id covers reconnect
   * replay-from-0-0. No replay — the transport holds no whole-event buffer (derived views own their
   * own state, e.g. AgentActivityEngine). Its only retained state is the dedup Set + connection status.
   */
  subscribe<T extends AgentStreamEventType>(
    type: T,
    listener: (event: EventOfType<T>) => void,
    options: { signal: AbortSignal }
  ): void {
    // Record the type and, if a source is already open (subscribe-after-connect), wire its native
    // listener now — #open() only wires the types known at open time, so a late subscriber needs this.
    if (!this.#subscribedTypes.has(type)) {
      this.#subscribedTypes.add(type);
      this.#addSourceListener(type);
    }
    const domListener = (event: Event): void => listener((event as CustomEvent<EventOfType<T>>).detail);
    this.#eventTarget.addEventListener(type, domListener, options);
  }

  #open(): void {
    this.#setStatus('connecting');
    this.#sourceListeners = new AbortController();
    const source = new EventSource(this.#url, { withCredentials: true });
    this.#eventSource = source;
    source.onopen = () => {
      this.#reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS; // reset backoff on a healthy open
      this.#setStatus('open');
    };
    source.onerror = () => {
      // Disable the browser's opaque native retry: close on ANY error and own the backoff. A closed
      // source won't fire onerror again, so exactly one reconnect is ever scheduled per open.
      this.#teardownSource();
      if (this.#intentionallyClosed) {
        return; // a deliberate close raced the error handler — do not reschedule
      }
      this.#setStatus('reconnecting');
      const delay = this.#reconnectDelayMs;
      this.#reconnectDelayMs = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
      this.#reconnectTimer = setTimeout(() => {
        this.#reconnectTimer = null;
        if (!this.#intentionallyClosed) {
          this.#open();
        }
      }, delay);
    };
    // Only wire native listeners for types with a subscriber (+ terminals). Unconsumed types are never
    // parsed or deduped, so #seenIds doesn't grow for events like text_delta that nothing renders.
    this.#subscribedTypes.forEach((type) => this.#addSourceListener(type));
  }

  /** Wires one native listener on the current source for a subscribed event type, scoped to the
   * per-source AbortController so #teardownSource removes it. No-op if no source is open — #open()
   * wires all subscribed types when the source is created. */
  #addSourceListener(type: AgentStreamEventType): void {
    const source = this.#eventSource;
    if (_notNil(source) && _notNil(this.#sourceListeners)) {
      source.addEventListener(type, (messageEvent: MessageEvent<string>): void => this.#handle(type, messageEvent), {
        signal: this.#sourceListeners.signal,
      });
    }
  }

  #clearReconnectTimer(): void {
    if (_notNil(this.#reconnectTimer)) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
  }

  #teardownSource(): void {
    if (_notNil(this.#eventSource)) {
      this.#sourceListeners?.abort(); // removes every native listener wired in #open/#addSourceListener
      this.#sourceListeners = null;
      this.#eventSource.close();
      this.#eventSource = null;
    }
  }

  #handle(channel: AgentStreamEventType, messageEvent: MessageEvent<string>): void {
    // EventSource always delivers string `data` (SSE spec), so the event is typed MessageEvent<string>
    // rather than the default MessageEvent<any> — no cast, no runtime string guard needed.
    const raw = messageEvent.data;
    // Parse to `unknown` and narrow structurally — JSON.parse yields null/array/string/number/boolean
    // for valid-but-non-object JSON, and the `as` below is only a compile-time assertion. Without the
    // isRecord guard, `value.type` on null throws OUTSIDE the try (escaping the handler) and spreading
    // a primitive is nonsense. This is a trusted server, so structural (not full-schema) validation.
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch (error) {
      this.#report('Failed to parse agent stream event', error);
      return;
    }
    if (!isRecord(value)) {
      this.#report('Agent stream event was not an object', new Error(raw));
      return;
    }
    if (value.type !== channel) {
      this.#report(`Agent stream event type did not match channel "${channel}"`, new Error(raw));
      return;
    }
    const id = messageEvent.lastEventId;
    if (id === '') {
      this.#report('Agent stream event missing lastEventId', new Error(raw));
      return;
    }
    if (this.#seenIds.has(id)) {
      return; // dedup: reconnect replay-from-0-0 is a no-op
    }
    this.#seenIds.add(id);
    const event = { ...value, id } as EventOfType<typeof channel>; // spread — don't mutate the parsed value
    this.#eventTarget.dispatchEvent(new CustomEvent(channel, { detail: event }));
    if (TERMINAL_STREAM_EVENTS.has(channel)) {
      this.disconnect(); // sets intentionallyClosed → no reconnect after a terminal event
    }
  }

  #setStatus(status: AgentStreamConnectionStatus): void {
    this.#status = status;
    this.#eventTarget.dispatchEvent(new Event(STATUS_CHANNEL));
  }

  #report(message: string, cause: unknown): void {
    ExceptionHandler.captureException(
      new InVivoError(message, {
        cause: cause instanceof Error ? cause : new Error(String(cause)),
        slug: 'agent-stream-parse',
        level: 'warning',
      })
    );
  }
}
